import {
  Card,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "../utils/axios-instance";
import { ServerRoutes } from "../routes";
import { CourseItem } from "../types";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const fetchCourseItems = async (courseId: number) => {
  const response = await axios.get<CourseItem[]>(
    `${ServerRoutes.courseItems}?courseId=${courseId}`
  );
  return response.data;
};

const emptyItem = {
  name: "",
  description: "",
  cover: "",
  type: "video",
  url: "",
  duration: "",
  priority: "",
};

export function CourseItemsSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { courseId, seasonId } = useParams();
  const numericCourseId = Number(courseId);
  const numericSeasonId = Number(seasonId);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CourseItem | null>(null);
  const [formValues, setFormValues] = useState(emptyItem);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["course-items", numericCourseId],
    queryFn: () => fetchCourseItems(numericCourseId),
    enabled: Number.isFinite(numericCourseId),
  });

  const allItems = data ?? [];
  const items = allItems.filter((item) => item.season?.id === numericSeasonId);
  const courseName = allItems[0]?.course?.name ?? `Course ${courseId}`;
  const seasonName =
    items[0]?.season?.name ??
    allItems.find((item) => item.season?.id === numericSeasonId)?.season?.name ??
    `Season ${seasonId}`;

  const openCreateDialog = () => {
    setFormValues(emptyItem);
    setIsCreateOpen(true);
  };

  const openEditDialog = (item: CourseItem) => {
    setSelectedItem(item);
    setFormValues({
      name: item.name ?? "",
      description: item.description ?? "",
      cover: item.cover ?? "",
      type: item.type ?? "video",
      url: item.url ?? "",
      duration: item.duration?.toString() ?? "",
      priority: item.priority?.toString() ?? "",
    });
    setIsEditOpen(true);
  };

  const openDeleteDialog = (item: CourseItem) => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };

  const closeCreateDialog = () => {
    if (!isSubmitting) {
      setIsCreateOpen(false);
    }
  };

  const closeEditDialog = () => {
    if (!isSubmitting) {
      setIsEditOpen(false);
    }
  };

  const closeDeleteDialog = () => {
    if (!isSubmitting) {
      setIsDeleteOpen(false);
    }
  };

  const handleFormChange = (field: keyof typeof emptyItem) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setFormValues((prev) => ({ ...prev, type: event.target.value }));
  };

  const handleEditItem = async () => {
    if (!selectedItem) return;
    setIsSubmitting(true);
    try {
      await axios.patch(`${ServerRoutes.courseItems}/${selectedItem.id}`, {
        courseId: numericCourseId,
        seasonId: numericSeasonId,
        name: formValues.name.trim(),
        description: formValues.description.trim(),
        cover: formValues.cover.trim(),
        type: formValues.type,
        url: formValues.url.trim(),
        duration: Number(formValues.duration) || 0,
        priority: Number(formValues.priority) || 0,
      });
      setSnackbarMessage("Item updated successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsEditOpen(false);
      setSelectedItem(null);
      refetch();
    } catch (error) {
      setSnackbarMessage("Failed to update item.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) return;
    setIsSubmitting(true);
    try {
      await axios.delete(`${ServerRoutes.courseItems}/${selectedItem.id}`);
      setSnackbarMessage("Item deleted successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsDeleteOpen(false);
      setSelectedItem(null);
      refetch();
    } catch (error) {
      setSnackbarMessage("Failed to delete item.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateItem = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(ServerRoutes.courseItems, {
        courseId: numericCourseId,
        seasonId: numericSeasonId,
        name: formValues.name.trim(),
        description: formValues.description.trim(),
        cover: formValues.cover.trim(),
        type: formValues.type,
        url: formValues.url.trim(),
        duration: Number(formValues.duration) || 0,
        priority: Number(formValues.priority) || 0,
      });
      setSnackbarMessage("Item created successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsCreateOpen(false);
      refetch();
    } catch (error) {
      setSnackbarMessage("Failed to create item.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Card sx={{ p: isMobile ? 2 : 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {courseName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {seasonName} Items
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(`/courses/${numericCourseId}`)}
          >
            Back to Seasons
          </Button>
          <Button variant="contained" onClick={openCreateDialog}>
            Add Item
          </Button>
        </Box>
      </Box>

      <Paper elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>URL</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Failed to load course items.
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No items found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.type}</TableCell>
                  <TableCell>{item.duration}</TableCell>
                  <TableCell>{item.priority}</TableCell>
                  <TableCell>{item.url}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => openEditDialog(item)} size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => openDeleteDialog(item)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={isCreateOpen} onClose={closeCreateDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Course Item</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formValues.name}
              onChange={handleFormChange("name")}
              fullWidth
            />
            <TextField
              label="Description"
              value={formValues.description}
              onChange={handleFormChange("description")}
              fullWidth
              multiline
              rows={3}
              helperText="You can use the [EXCHANGE_REFERAL_URL] for exchange referal url."
            />
            <TextField
              label="Cover URL"
              value={formValues.cover}
              onChange={handleFormChange("cover")}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="item-type-label">Type</InputLabel>
              <Select
                labelId="item-type-label"
                label="Type"
                value={formValues.type}
                onChange={handleTypeChange}
              >
                <MenuItem value="video">Video</MenuItem>
                <MenuItem value="audio">Audio</MenuItem>
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="document">Document</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="URL"
              value={formValues.url}
              onChange={handleFormChange("url")}
              fullWidth
            />
            <TextField
              label="Duration (minutes)"
              value={formValues.duration}
              onChange={handleFormChange("duration")}
              fullWidth
              type="number"
            />
            <TextField
              label="Priority"
              value={formValues.priority}
              onChange={handleFormChange("priority")}
              fullWidth
              type="number"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateItem}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isEditOpen} onClose={closeEditDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit Course Item</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formValues.name}
              onChange={handleFormChange("name")}
              fullWidth
            />
            <TextField
              label="Description"
              value={formValues.description}
              onChange={handleFormChange("description")}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Cover URL"
              value={formValues.cover}
              onChange={handleFormChange("cover")}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="item-type-edit-label">Type</InputLabel>
              <Select
                labelId="item-type-edit-label"
                label="Type"
                value={formValues.type}
                onChange={handleTypeChange}
              >
                <MenuItem value="video">Video</MenuItem>
                <MenuItem value="audio">Audio</MenuItem>
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="document">Document</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="URL"
              value={formValues.url}
              onChange={handleFormChange("url")}
              fullWidth
            />
            <TextField
              label="Duration (minutes)"
              value={formValues.duration}
              onChange={handleFormChange("duration")}
              fullWidth
              type="number"
            />
            <TextField
              label="Priority"
              value={formValues.priority}
              onChange={handleFormChange("priority")}
              fullWidth
              type="number"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditItem}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isDeleteOpen} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Delete Course Item</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedItem?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteItem}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
}

